import { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useLoggedinUser, useService } from '@hooks';
import { RxUtils, Tr } from '@utils';
import { notificationService, User, UserInvitation, userInvitationService, UserInvitationState, userService } from '@services';
import { collaboratorsStatusTabSelector, setCollaboratorsStatusTab } from '@store/slices/portal.slice';
import { Button, Stack, Tab, Tabs } from '@mui/material';
import { EmptyEntityPageLayout, PortalPageTitle } from '@components/portal/common';
import { CollaboratorList } from '@components/portal/collaborators';
import { ReactComponent as Illustration } from '@assets/illustrations/no-collaborators-added.svg';
import { EntityDeleteConfirmationDialog } from '@components/common';

const paddingX = 9;
type EntityType = UserInvitation | User;

export const Collaborators: FC = () => {
	const { t } = useTranslation('portal');
	const user = useLoggedinUser();
	const navigate = useNavigate();
	const dispatch = useDispatch();
	const collaboratorsStatusTab = useSelector(collaboratorsStatusTabSelector);
	const [deletingEntities, setDeletingEntities] = useState<EntityType[]>([]);
	const [selectedEntities, setSelectedEntities] = useState<EntityType[]>([]);
	const { data: users = [], trigger: userTrigger } = useService(() => userService.getAll());
	const { data: invitations = [], trigger: invitationTrigger } = useService(() =>
		userInvitationService.getAll(UserInvitationState.PENDING)
	);
	const isInvitationList = collaboratorsStatusTab === UserInvitationState.PENDING;
	const allEntities = [...invitations, ...users];
	const entities = isInvitationList ? invitations : users;

	const getService = () => (isInvitationList ? userInvitationService : userService);
	const clearSelected = () => setSelectedEntities([]);
	const onAdd = () => navigate('./add-collaborator');
	const onEdit = (d: EntityType) => navigate(`./${d instanceof User ? 'edit-collaborator' : 'edit-invitation'}/${d.email}`);
	const onDelete = (email: string) => RxUtils.promisify(getService().delete(email), () => (invitationTrigger(), userTrigger()));
	const onDeleteMultiple = (emails: string[]) =>
		RxUtils.promisify(getService().deleteMultiple(emails), () => (invitationTrigger(), userTrigger()));
	const onResendInvitation = (entity: EntityType) =>
		RxUtils.promisify(userInvitationService.resendInvitation(entity.email), () =>
			notificationService.sendSuccess(t('collaborators.resend-successful-message'))
		);

	useEffect(clearSelected, [collaboratorsStatusTab]);

	return (
		<>
			{Array.isArray(allEntities) && allEntities.length ? (
				<Stack direction="column" spacing={3} sx={{ height: 1.0, maxHeight: 1.0, paddingY: 5 }}>
					<PortalPageTitle
						paddingX={paddingX}
						title={<Tr.Portal path="collaborators.title" />}
						subtitle={<Tr.Portal path="collaborators.subtitle" />}
					/>
					<Stack direction="column" sx={{ flexGrow: 1, maxHeight: 1.0, overflow: 'hidden' }}>
						<Tabs value={collaboratorsStatusTab} onChange={(_e, val) => dispatch(setCollaboratorsStatusTab(val))} centered>
							<Tab value={UserInvitationState.ACCEPTED} label={<Tr.Portal path="collaborators.tab-accepted" />} />
							<Tab value={UserInvitationState.PENDING} label={<Tr.Portal path="collaborators.tab-pending" />} />
						</Tabs>
						<CollaboratorList
							paddingX={paddingX}
							entities={entities}
							selected={selectedEntities}
							empty={<Illustration />}
							onSelectChange={setSelectedEntities}
							onEdit={onEdit}
							onDelete={(entity) => setDeletingEntities([entity])}
							onResendInvitation={isInvitationList ? onResendInvitation : undefined}
						/>
					</Stack>
					<Stack direction="row" spacing={3} justifyContent="center" sx={{ paddingX }}>
						{selectedEntities.length ? (
							<>
								<Button
									data-testid="delete-collaborator-modal-open-button"
									variant="contained"
									color="secondary"
									sx={{ textTransform: 'uppercase' }}
									onClick={() => setDeletingEntities(selectedEntities)}
								>
									<Tr.Portal path="delete" />
								</Button>
								<Button
									data-testid="cancel-collaborator-selection-button"
									variant="outlined"
									color="secondary"
									sx={{ textTransform: 'uppercase' }}
									onClick={clearSelected}
								>
									<Tr.Portal path="cancel" />
								</Button>
							</>
						) : (
							<>
								{user?.canManageCollaborators() && (
									<Button
										data-testid="add-collaborators-modal-open-button"
										variant="contained"
										color="secondary"
										onClick={onAdd}
									>
										<Tr.Portal path="collaborators.add-collaborator" />
									</Button>
								)}
								<Button
									data-testid="manage-roles-navigation-button"
									variant="outlined"
									color="secondary"
									onClick={() => navigate('./roles')}
								>
									<Tr.Portal path="collaborators.manage-roles" />
								</Button>
							</>
						)}
					</Stack>
				</Stack>
			) : (
				<EmptyEntityPageLayout
					illustration={<Illustration />}
					title={<Tr.Portal path="collaborators.no-collaborators-added-title" />}
					addButtonText={<Tr.Portal path="collaborators.add-collaborator" />}
					onAddButtonClick={onAdd}
					addButtonProps={{ 'data-testid': 'add-collaborators-modal-open-button' }}
				/>
			)}
			<EntityDeleteConfirmationDialog
				open={!!deletingEntities?.length}
				content={<Tr.Portal path="collaborators.delete-confirmation-message" />}
				buttonProps={{ 'data-testid': 'collaborators-delete-confirmation-button' }}
				onClose={() => (setDeletingEntities([]), clearSelected())}
				onAccept={() => {
					deletingEntities.length > 1
						? onDeleteMultiple?.(deletingEntities.map((entity) => entity.email))
						: onDelete?.(deletingEntities[0].email);
					setDeletingEntities([]);
					clearSelected();
				}}
			>
				{deletingEntities.map((e) => `"${e.email}"`).join(', ')}
			</EntityDeleteConfirmationDialog>
		</>
	);
};
